import React from 'react'
import { Card, IconButton } from '../../ui'
import { DotScale } from './interactions/DotScale.jsx'
import { SliderScale } from './interactions/SliderScale.jsx'
import { SegmentedBar } from './interactions/SegmentedBar.jsx'
import { QuadrantPicker } from './interactions/QuadrantPicker.jsx'
import { EnergyBatteryScale } from './interactions/EnergyBatteryScale.jsx'
import { WeekRatingWeatherScale } from './interactions/WeekRatingWeatherScale.jsx'
import { ClarityRingsScale } from './interactions/ClarityRingsScale.jsx'
import { WorkloadGaugeScale } from './interactions/WorkloadGaugeScale.jsx'
import { BulletList } from './interactions/BulletList.jsx'
import { BulletListEnriched } from './interactions/BulletListEnriched.jsx'
import { BulletListKudos } from './interactions/BulletListKudos.jsx'
import { KudosList } from './interactions/KudosList.jsx'
import { migrateKudosData } from './interactions/kudosMigration.js'
import { EnrichedBulletList } from './interactions/EnrichedBulletList.jsx'
import { migrateItems } from './interactions/itemMigration.js'
import { useGoals } from '../../goals/hooks/useGoals.js'
import { ChipSelector } from './interactions/ChipSelector.jsx'
import { ChipMultiSelectWithContext } from './interactions/ChipMultiSelectWithContext.jsx'
import { migrateChipContextData } from './interactions/chipContextMigration.js'
import { MoodCompassGrid } from './interactions/MoodCompassGrid.jsx'
import { migrateMoodCompassData } from './interactions/moodCompassMigration.js'
import { SegmentedToggle } from './interactions/SegmentedToggle.jsx'
import { FeedbackDirectionToggle } from './interactions/FeedbackDirectionToggle.jsx'
import { migrateFeedbackDirectionData } from './interactions/feedbackDirectionMigration.js'
import { SingleInput } from './interactions/SingleInput.jsx'
import { ThreeFieldReflection } from './interactions/ThreeFieldReflection.jsx'
import { NumberedBulletList } from './interactions/NumberedBulletList.jsx'
import { PrioritiesList } from './interactions/PrioritiesList.jsx'
import { migratePrioritiesData } from './interactions/prioritiesMigration.js'

const PREP_COLOR = '#3b82c4'

function resolveComponent(type, subType) {
  if (type === 'quick-pulse') {
    if (subType === 'dots') return DotScale
    if (subType === 'slider') return SliderScale
    if (subType === 'segmented') return SegmentedBar
    if (subType === 'quadrant') return QuadrantPicker
    if (subType === 'battery') return EnergyBatteryScale
    if (subType === 'weather') return WeekRatingWeatherScale
    if (subType === 'rings') return ClarityRingsScale
    if (subType === 'gauge') return WorkloadGaugeScale
  }
  if (type === 'bullet-list' && subType === 'enriched') return EnrichedBulletList
  if (type === 'bullet-list') return BulletList
  if (type === 'bullet-list-enriched') return BulletListEnriched
  if (type === 'bullet-list-kudos') return BulletListKudos
  if (type === 'kudos') return KudosList
  if (type === 'priorities') return PrioritiesList
  if (type === 'bullet-list-numbered') return NumberedBulletList
  if (type === 'chip-multi-context') return ChipMultiSelectWithContext
  if (type === 'selection-note') {
    if (subType === 'chips') return ChipSelector
    if (subType === 'toggle') return SegmentedToggle
  }
  if (type === 'feedback-direction') return FeedbackDirectionToggle
  if (type === 'mood-compass') return MoodCompassGrid
  if (type === 'reflection') {
    if (subType === 'single') return SingleInput
    if (subType === 'three-field') return ThreeFieldReflection
  }
  return null
}

function buildProps(cardDef, data, onChange, disabled) {
  const { type, subType, config } = cardDef
  const base = { data, onChange, disabled }

  if (type === 'quick-pulse' && subType === 'dots') {
    return { value: data, onChange, disabled, ...config }
  }
  if (type === 'quick-pulse' && subType === 'slider') {
    return { value: data, onChange, disabled, ...config }
  }
  if (type === 'quick-pulse' && subType === 'segmented') {
    return { value: data, onChange, disabled, ...config }
  }
  if (type === 'quick-pulse' && subType === 'quadrant') {
    return { value: data, onChange, disabled, ...config }
  }
  if (type === 'quick-pulse' && subType === 'battery') {
    return { value: data, onChange, disabled }
  }
  if (type === 'quick-pulse' && subType === 'weather') {
    return { value: data, onChange, disabled }
  }
  if (type === 'quick-pulse' && subType === 'rings') {
    return { value: data, onChange, disabled }
  }
  if (type === 'quick-pulse' && subType === 'gauge') {
    return { value: data, onChange, disabled }
  }
  if (type === 'bullet-list' && subType === 'enriched') {
    return { items: migrateItems(data?.items ?? []), onChange, disabled, ...config }
  }
  if (type === 'bullet-list') {
    return { items: data?.items ?? [], onChange, disabled, ...config }
  }
  if (type === 'bullet-list-enriched') {
    return { items: data?.items ?? [], onChange, disabled, ...config }
  }
  if (type === 'bullet-list-kudos') {
    return { items: data?.items ?? [], onChange, disabled, ...config }
  }
  if (type === 'kudos') {
    return { value: migrateKudosData(data), onChange, disabled, ...config }
  }
  if (type === 'priorities') {
    return { value: migratePrioritiesData(data), onChange, disabled, ...config }
  }
  if (type === 'bullet-list-numbered') {
    return { items: data?.items ?? [], onChange, disabled, ...config }
  }
  if (type === 'feedback-direction') {
    return { value: migrateFeedbackDirectionData(data), onChange, disabled, maxContextChars: config?.maxContextChars ?? 200 }
  }
  if (type === 'mood-compass') {
    return { value: migrateMoodCompassData(data), onChange, disabled }
  }
  if (type === 'chip-multi-context') {
    const migrated = migrateChipContextData(data)
    return { value: migrated, onChange, chips: config.chips, maxSelections: config.maxSelections, maxContextChars: config.maxContextChars, disabled }
  }
  if (type === 'selection-note' && subType === 'chips') {
    return { chips: config.chips, selections: data?.selections ?? [], onChange, maxNoteChars: config.maxNoteChars, disabled }
  }
  if (type === 'selection-note' && subType === 'toggle') {
    return { options: config.options, value: data, onChange, maxNoteChars: config.maxNoteChars, disabled }
  }
  if (type === 'reflection' && subType === 'single') {
    return { value: data, onChange, disabled, ...config }
  }
  if (type === 'reflection' && subType === 'three-field') {
    return { fields: config.fields, values: data ?? {}, onChange, maxChars: config.maxChars, disabled }
  }
  return base
}

export function PrepCard({ cardDef, data, onChange, disabled = false, onRemove, ownerUserId = null, onHeaderClick }) {
  const needsGoals = cardDef.config?.suffixType === 'goal-link' || cardDef.type === 'priorities'
  const { goals } = useGoals({ ownerUserId, enabled: needsGoals && !!ownerUserId })
  const activeGoals = needsGoals ? (goals || []).filter((g) => g.status === 'active').map((g) => ({ goalId: g.goalId, title: g.title })) : []

  const Component = resolveComponent(cardDef.type, cardDef.subType)
  const props = buildProps(cardDef, data, onChange, disabled)

  if (cardDef.config?.suffixType === 'goal-link') {
    props.suffixContext = { activeGoals }
  }
  if (cardDef.type === 'priorities') {
    props.activeGoals = activeGoals
  }

  return (
    <Card
      variant={disabled ? 'readonly' : 'default'}
      data-testid={`prep-card-${cardDef.id}`}
      style={{
        padding: 0,
        minHeight: 216,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={styles.header}>
        {onHeaderClick ? (
          <button
            onClick={onHeaderClick}
            onMouseDown={(e) => e.stopPropagation()}
            onDragStart={(e) => e.preventDefault()}
            aria-label={`View ${cardDef.label} history`}
            style={styles.headerClickable}
          >
            <span style={{ ...styles.iconBadge, background: PREP_COLOR + '18', color: PREP_COLOR }}>{cardDef.icon ?? '⚡'}</span>
            <div style={styles.headerText}>
              <span style={styles.label}>{cardDef.label}</span>
              <span style={styles.prompt}>{cardDef.prompt}</span>
            </div>
          </button>
        ) : (
          <>
            <span style={{ ...styles.iconBadge, background: PREP_COLOR + '18', color: PREP_COLOR }}>{cardDef.icon ?? '⚡'}</span>
            <div style={styles.headerText}>
              <span style={styles.label}>{cardDef.label}</span>
              <span style={styles.prompt}>{cardDef.prompt}</span>
            </div>
          </>
        )}
        <span style={{ flex: 1 }} />
        {!disabled && <span style={styles.grip} aria-hidden>⠿</span>}
        {onRemove && !disabled && (
          <IconButton ariaLabel={`Remove ${cardDef.label}`} onClick={() => onRemove(cardDef.id)}>×</IconButton>
        )}
      </div>
      <div style={{ ...styles.accentBar, background: PREP_COLOR }} />
      {Component && <Component {...props} />}
    </Card>
  )
}

const styles = {
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '12px 12px 0',
  },
  iconBadge: {
    width: '28px', height: '28px', borderRadius: '6px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '13px', flexShrink: 0,
  },
  headerText: { display: 'flex', flexDirection: 'column', gap: '1px' },
  label: { fontSize: '12px', fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--text-primary)' },
  prompt: { fontSize: '11px', color: 'var(--text-secondary)', opacity: 0.8 },
  headerClickable: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    textAlign: 'left',
  },
  grip: { fontSize: '12px', opacity: 0.2, cursor: 'grab', userSelect: 'none', marginTop: '2px' },
  accentBar: {
    height: '3px', opacity: 0.25, margin: '6px 12px',
    borderRadius: '2px',
  },
}
